import { Observable } from '@apollo/client';
import type { NextLink, Operation } from '@apollo/client';
import type { User } from 'oidc-client-ts';

export type RetryAuthorizedDeps = {
  refreshSession: () => Promise<User | null>
  getSession: () => User | null
  forceLogoutRedirect: () => Promise<void>
};

// OAuth error codes that mean the session genuinely needs an interactive
// re-login (the refresh is dead). Everything else — silent-renew iframe
// timeouts, network blips, state/nonce races between concurrent renews — is
// transient and must NOT log a still-valid session out.
const TERMINAL_AUTH_ERRORS = [
  'login_required',
  'interaction_required',
  'consent_required',
  'invalid_grant',
  'access_denied'
];

export const isInteractiveLoginRequired = (error: unknown): boolean => {
  const e = error as { error?: string; message?: string } | undefined;
  const code = e?.error ?? '';
  const message = e?.message ?? '';
  return TERMINAL_AUTH_ERRORS.some((t) => code === t || message.includes(t));
};

/**
 * Builds the retry-once-on-401 handler. Kept in its own dependency-light module
 * so the refresh-and-replay behavior is unit-testable without dragging in the
 * OIDC manager or the GraphQL operation graph.
 *
 * On an unauthorized response it refreshes the session, then re-stamps the
 * freshly-refreshed token onto the operation before replaying. The re-stamp is
 * essential: `forward()` from errorLink only re-runs links DOWNSTREAM of it,
 * and authMiddleware (which normally sets the auth header) sits upstream — so
 * without this the replay would resend the expired token.
 *
 * On refresh failure it ONLY redirects to login when the error genuinely needs
 * interactive re-auth (see isInteractiveLoginRequired). A transient silent-
 * renew failure surfaces as an operation error without logging the user out —
 * a still-valid session shouldn't die because one silent renew hiccuped.
 */
export const createRetryAuthorized = (deps: RetryAuthorizedDeps) => (
  operation: Operation,
  forward: NextLink
) => new Observable<any>((observer) => {
  deps.refreshSession()
    .then(() => {
      const fresh = deps.getSession();
      if (fresh?.access_token) {
        operation.setContext(({ headers = {} }: { headers?: Record<string, any> }) => ({
          headers: {
            ...headers,
            Authorization: `Bearer ${fresh.access_token}`
          }
        }));
      }
      forward(operation).subscribe({
        next: observer.next.bind(observer),
        error: observer.error.bind(observer),
        complete: observer.complete.bind(observer)
      });
    })
    .catch(async (error: unknown) => {
      // Only bounce to login on a genuine re-auth requirement; a transient
      // refresh failure fails just this operation and keeps the session.
      if (isInteractiveLoginRequired(error)) {
        await deps.forceLogoutRedirect();
      }
      observer.error(error);
    });
});
